import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth, isStaff } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/public/Brand";
import { ThemeToggle } from "@/components/public/PublicLayout";
import { useSettings } from "@/hooks/useSettings";

export default function Login() {
  const { login } = useAuth();
  const { settings } = useSettings();
  const logo = settings?.logo;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { identifier, password });
      login(r.data.token, r.data.user);
      toast.success(`Welcome back, ${r.data.user.name}`);
      if (next) navigate(next);
      else navigate(isStaff(r.data.user) ? "/admin" : "/portal");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setBusy(false);
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden bg-foreground lg:block">
        <img src="https://images.pexels.com/photos/16367693/pexels-photo-16367693.jpeg" alt="Shopping" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-black/40" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center text-white">
          <div className="rounded-2xl bg-white/95 p-4 shadow-xl">
            {logo ? <img src={logo} alt="MANHA DIGITAL" className="h-16 w-auto max-w-[220px] object-contain" data-testid="login-logo" />
              : <span className="grid h-16 w-16 place-items-center rounded-xl bg-primary font-display text-3xl font-extrabold text-primary-foreground">M</span>}
          </div>
          <h2 className="mt-6 font-display text-3xl font-extrabold tracking-tight">MANHA DIGITAL</h2>
          <p className="mt-1 whitespace-nowrap text-white/80">Your Dreams | Our Deals</p>
        </div>
      </div>

      <div className="flex flex-col p-6 sm:p-12">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Brand height={38} textClass="font-display text-lg font-extrabold" />
          </Link>
          <ThemeToggle />
        </div>

        <div className="my-auto w-full max-w-sm rounded-2xl border border-border bg-card p-6 shadow-lg py-8 lg:border-0 lg:bg-transparent lg:p-0 lg:py-10 lg:shadow-none">
          <div className="mb-6 flex flex-col items-center text-center lg:hidden">
            {logo ? <img src={logo} alt="MANHA DIGITAL" className="h-14 w-auto max-w-[200px] object-contain" data-testid="login-logo-mobile" />
              : <span className="grid h-14 w-14 place-items-center rounded-xl bg-primary font-display text-2xl font-extrabold text-primary-foreground">M</span>}
            <h2 className="mt-3 font-display text-2xl font-extrabold">MANHA DIGITAL</h2>
            <p className="text-sm text-muted-foreground whitespace-nowrap">Your Dreams | Our Deals</p>
          </div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Sign In</h1>
          <p className="mt-1 text-sm text-muted-foreground">Enter your credentials to access your account.</p>

          <form onSubmit={submit} className="mt-8 grid gap-4">
            <div>
              <Label>Username or Email</Label>
              <Input required value={identifier} onChange={(e) => setIdentifier(e.target.value)} placeholder="you@example.com" data-testid="login-identifier" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label>Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-primary hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" />
            </div>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="login-submit-btn">{busy ? "Signing in..." : "Sign In"}</Button>
          </form>

          <p className="mt-8 text-center text-sm text-muted-foreground">Don't have an account? <Link to="/signup" className="font-semibold text-primary hover:underline">Create one</Link></p>
        </div>
      </div>
    </div>
  );
}
